import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getChatMembers,
  searchChatMembers,
  updateChatMembers,
} from "controller/api/chat/chatMembers";
import { generateQueryKey } from "controller/cache/helpers/queryKeyGenerator";
import type {
  ChatMember,
  ChatMemberSearchResult,
  UpdateMembersInput,
  UpdateMembersResponse,
} from "shared";

/**
 * Query key helpers for chat members
 */
export const chatMembersKeys = {
  all: () => generateQueryKey({ entity: "chat-members" }),
  list: (serviceId: string, threadId?: string) =>
    generateQueryKey({
      entity: "chat-members",
      operation: "list",
      filters: { serviceId, threadId },
    }),
  search: (serviceId: string, search: string, threadId?: string) =>
    generateQueryKey({
      entity: "chat-members",
      operation: "list",
      filters: { serviceId, search, threadId },
    }),
};

/**
 * Get all chat members for a service/ticket
 */
export const useGetChatMembers = (serviceId: string, threadId?: string) => {
  return useQuery<ChatMember[], Error>({
    queryKey: chatMembersKeys.list(serviceId, threadId),
    queryFn: () => getChatMembers(serviceId, threadId),
    enabled: Boolean(serviceId),
  });
};

/**
 * Search for users to add as chat members
 */
export const useSearchChatMembers = (
  serviceId: string,
  search: string,
  threadId?: string,
) => {
  return useQuery<ChatMemberSearchResult[], Error>({
    queryKey: chatMembersKeys.search(serviceId, search, threadId),
    queryFn: () => searchChatMembers(serviceId, search, threadId),
    enabled: Boolean(serviceId && search && search.length >= 2),
  });
};

/**
 * Update chat members — combined add + remove in a single mutation
 */
export const useUpdateChatMembers = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdateMembersResponse, Error, UpdateMembersInput>(
    (input) => updateChatMembers(input),
    {
      onSuccess(_data, variables) {
        queryClient.invalidateQueries({
          queryKey: chatMembersKeys.list(
            variables.service_id,
            variables.thread_id,
          ),
        });
      },
    },
  );
};
